import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '../../globals';
import { recordToQueryParams } from '../../helpers';
import { ClientVersionService } from '../client-version/client-version.service';
import { Group } from '../groups/groups.interfaces';
import { GroupParent, GroupsAddResponse } from './group-parents.interface';

@Injectable({
  providedIn: 'root',
})
export class GroupParentsService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly clientVersionService: ClientVersionService =
    inject(ClientVersionService);

  /**
   * Get all group parents
   * @returns
   */
  getAll(options?: {
    includeChildren?: boolean;
    includeFunctionBlocks?: boolean;
    excludeNotVisible?: boolean;
    excludeShared?: boolean;
  }): Observable<GroupParent[]> {
    const clientVersion = this.clientVersionService.getVersion();
    const queryParams = recordToQueryParams({
      ...options,
      clientVersion,
    });

    const url = `${this.globals.apiRoot}/jwtGroupParents${queryParams}`;
    return this.http.get<GroupParent[]>(url);
  }

  getById(parentId: number): Observable<GroupParent> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}`;
    return this.http.get<GroupParent>(url);
  }

  add(groupParent: GroupParent): Observable<GroupParent> {
    const url = `${this.globals.apiRoot}/jwtGroupParents`;
    return this.http.post<GroupParent>(url, groupParent);
  }

  addParentAndChild(parent: GroupParent, child: Group): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/children`;
    return this.http.post<number>(url, { parent, child });
  }

  addGroupToParent(parentId: number, group: Group): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}/children`;
    return this.http.post<number>(url, group);
  }

  update(parentId: number, groupParent: GroupParent): Observable<GroupParent> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}`;
    return this.http.put<GroupParent>(url, groupParent);
  }

  delete(parentId: number): Observable<GroupParent> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}`;
    return this.http.delete<GroupParent>(url);
  }

  /**
   * Set the visiblity of the group parent.
   * @param parentId
   * @param visible
   * @returns
   */
  setVisibleGroupParent(
    parentId: number,
    visible: boolean,
  ): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}/visible`;
    return this.http.put<boolean>(url, visible);
  }

  /***
   * Set the visibility of a child of the group parent.
   */
  setVisibleGroupChild(
    parentId: number,
    childId: number,
    visible: boolean,
  ): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}/children/${childId}/visible`;
    return this.http.put<boolean>(url, visible);
  }

  /**
   * Move a child in a group parent
   * or to another group parent.
   *
   * @param childId
   * @param newParentId
   * @param newOrder
   * @returns An array containing the (new) parent id, child id and (new) order of the groups of the user where the order has changed.
   */
  moveChild(childId: number, newParentId: number, newOrder: number) {
    const url = `${this.globals.apiRoot}/jwtGroupParents/children/${childId}/move`;
    return this.http.put<
      {
        readonly parentId: number;
        readonly childId: number;
        readonly order: number;
      }[]
    >(url, { newParentId, newOrder });
  }

  /**
   * Change the order of a group parent.
   *
   * @param parentId id of the group parent to move
   * @param newOrder the new order of the parent
   * @returns An array containing the id and new order of the group parents where the order has been updated.
   */
  moveParent(
    parentId: number,
    newOrder: number,
  ): Observable<{ readonly id: number; readonly order: number }[]> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}/move`;
    return this.http.put<{ readonly id: number; readonly order: number }[]>(
      url,
      newOrder,
    );
  }

  addGroupsToParent(
    parentId: number,
    groups: Group[],
  ): Observable<GroupsAddResponse> {
    const url = `${this.globals.apiRoot}/jwtGroupParents/${parentId}/children/bulk`;
    return this.http.post<GroupsAddResponse>(url, groups);
  }

  getByInstallationId(installationId: number): Observable<GroupParent[]> {
    const url = `${this.globals.apiRoot}/jwtGroupParents?includeFunctionBlocks=false&excludeNotVisible=true&clientVersion=3.5.1&includeChildren=true&installationId=${installationId}`;
    return this.http.get<GroupParent[]>(url);
  }
}
